import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import appServices from "../../services/app-services/appServices";
import { convertUserImage } from "../../util/utils";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import GroupIconImage from "../../components/GreenCity/GroupIconImage";
import LoadingModal from "../../components/LoadingModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import { Avatar, Box, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { colors } from "../../common/colors";
import dayjs from "dayjs";

export default function GroupMemberFee() {
  const navigate = useNavigate();
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const [paymentStatuses, setPaymentStatuses] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showErrModal, setShowErrModal] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    appServices.getGroupPayments(groupInfo?.id)
      .then((res: any) => {
        setLoading(false);
        setPaymentStatuses(res);
      })
      .catch((error: any) => {
        const errorResponse = error?.response?.data?.message

        if (errorResponse) {
          setErrMsg(typeof errorResponse === "object" ?
            errorResponse.join("\n") :
            errorResponse
          )
        } else {
          setErrMsg(T("api_unknown_error"))
        }
        setShowErrModal(true)
      })
      .finally(() => setLoading(false));
  }, []);

  const closeErrorModal = () => {
    setShowErrModal(false)
    navigate(-1)
  }

  return (
    <Box>
      <Box style={style.headerContainer}>
        <LoadingModal visible={loading} />

        <GCBackButton onClick={() => navigate(-1)} />
        <Box style={style.headerContent}>
          <Box style={style.eventPostingBox}>
            <Box component="div" style={style.eventPostingIcon}>
              <GroupIconImage groupId={groupInfo?.id} alt={groupInfo?.name} style={style.groupIcon} />
            </Box>
            <Typography style={style.paymentStatus}>{T("payment_status")}</Typography>
          </Box>
        </Box>
      </Box>

      <Box style={style.container}>
        <div style={style.tableContainer}>
          <table style={style.table}>
            <thead>
              <tr>
                <th style={{ ...style.tableHeaderCell, ...style.firstColoumn }}>
                  <div style={style.tableHeaderDiv}>
                    <Typography style={style.monthlyText}>
                      {groupInfo?.isMonthly ? T("month") : T("year")}
                      {T("membership_fee")}
                    </Typography>
                  </div>
                </th>
                {[...Array(3)].map((_: any, i: number) => (
                  <th key={`period_${i}`} style={style.tableHeaderCell}>
                    <Typography sx={style.name}>
                      {dayjs(paymentStatuses?.payment_dates[i]).format(groupInfo?.isMonthly ? "M" : "YYYY")}
                      {groupInfo?.isMonthly && T("member_payment_months")}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paymentStatuses?.user_status?.map((paymentUser: any, index: number) => (
                <tr key={index}>
                  <td style={{ ...style.tableCell, borderLeft: "0px" }}>
                    <div style={style.tableCellContent}>
                      <Typography sx={style.name}>{index + 1}</Typography>
                      <Avatar
                        src={paymentUser?.image ? convertUserImage(paymentUser?.image): ""}
                        alt={"img"}
                        style={style.avatar}
                      />
                      <Typography sx={style.name}>{paymentUser?.nickname}</Typography>
                    </div>
                  </td>
                  {paymentUser.payments_status.map((status: any, idx: number) => (
                    <td key={`status_${idx}`} style={style.tableCell}>
                      <CheckCircleOutlineIcon
                        style={{
                          ...style.checkIcon,
                          color: status.is_paid ? colors.farm_green500 : colors.farm_gray200
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>

      <ErrorMessageModal
        open={showErrModal}
        messages={[errMsg]}
        onClose={closeErrorModal}
      />
    </Box>
  );
}

const style = {
  headerContainer: {
    padding: "90px 10px 12px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: "10px",
  },
  eventPostingBox: {
    display: "flex",
    gap: "15px",
    alignItems: "center",
    paddingTop: "10px",
    paddingLeft: "10px",
  },
  eventPostingIcon: {
    borderRadius: "50%",
    color: "white",
  },
  groupIcon: {
    width: 30,
    height: 30,
    borderRadius: '50%',
  },
  paymentStatus: {
    fontWeight: "700",
    lineHeight: "30px",
  },
  container: {
    backgroundColor: "#ECEFF1",
    padding: "10px 20px",
    flex: 1,
  },
  tableContainer: {
    overflowX: "auto" as const,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
  },
  tableHeaderCell: {
    width: "20%",
    textAlign: "center" as const,
    padding: "8px 0",
    borderLeft: "1px solid #BCBCBC",
    borderBottom: `2px solid ${colors.farm_green500}`,
  },
  tableHeaderDiv: {
    maxWidth: "100px",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #BCBCBC",
  },
  monthlyText: {
    backgroundColor: colors.farm_white,
    textAlign: "center" as const,
  },
  tableCell: {
    padding: "8px",
    textAlign: "center" as const,
    borderTop: "1px solid #BCBCBC",
    borderLeft: "1px solid #BCBCBC",
  },
  tableCellContent: {
    display: "flex",
    alignItems: "center",
  },
  avatar: {
    width: 30,
    height: 30,
    margin: "0px 10px",
  },
  checkIcon: {
    fontSize: "26px",
    fontWeight: "400",
  },
  firstColoumn: {
    borderLeft: "0px",
    width: "40%",
  },
  name: {
    fontSize: "12px",
  },
};
